import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Stack,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../../services/api';
import ApprovalsQueueTable from '../ApprovalsQueueTable';
import { useSidebarActions } from '../SidebarActionsContext';

const STATUS_TABS = [
  { key: 'pending', label: 'Pending Approval', endpoint: '/admin/vendors/pending' },
  { key: 'incomplete', label: 'Incomplete', endpoint: '/admin/vendors/incomplete' },
  { key: 'denied', label: 'Denied', endpoint: '/admin/vendors/denied' },
];

const ApprovalsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || 'pending';
  const initialIndex = Math.max(0, STATUS_TABS.findIndex((t) => t.key === initialStatus));

  const [tabIndex, setTabIndex] = useState(initialIndex);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkMarkAsPaid, setBulkMarkAsPaid] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const { setActions } = useSidebarActions();

  const currentTab = STATUS_TABS[tabIndex];

  const load = useCallback(async () => {
    setLoading(true);
    setSelected([]);
    try {
      const res = await api.get(currentTab.endpoint);
      setRows(res.data);
    } catch (err) {
      console.error('Failed to load approvals queue:', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [currentTab.endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const next = STATUS_TABS[tabIndex]?.key || 'pending';
    if ((searchParams.get('status') || 'pending') !== next) {
      const sp = new URLSearchParams(searchParams);
      sp.set('status', next);
      setSearchParams(sp, { replace: true });
    }
  }, [tabIndex, searchParams, setSearchParams]);

  const handleBulkApprove = async () => {
    setBulkLoading(true);
    setBulkError('');
    try {
      await api.post('/admin/vendors/bulk-approve', {
        vendorIds: selected,
        notes: bulkNotes,
        markAsPaid: bulkMarkAsPaid || undefined,
      });
      setBulkOpen(false);
      setBulkNotes('');
      setBulkMarkAsPaid(false);
      setSelected([]);
      await load();
    } catch (err) {
      setBulkError(err.response?.data?.error || 'Failed to bulk approve');
    } finally {
      setBulkLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const parts = [
        r.business_name,
        r.email,
        r.contact_email,
        r.firstName,
        r.lastName,
        r.first_name,
        r.last_name,
        r.contact_first_name,
        r.contact_last_name,
        `${r.firstName || r.first_name || ''} ${r.lastName || r.last_name || ''}`,
        `${r.contact_first_name || ''} ${r.contact_last_name || ''}`,
        r.booth_name,
        r.phone,
        r.description,
        r.website,
        r.address,
        r.city,
        r.state,
        r.zip_code,
        r.category_name,
        r.special_requests,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return parts.includes(q);
    });
  }, [rows, search]);

  useEffect(() => {
    if (currentTab.key === 'pending' && selected.length > 0) {
      setActions([
        {
          key: 'bulk-approve',
          label: `Bulk approve (${selected.length})`,
          icon: <CheckCircleIcon />,
          variant: 'contained',
          color: 'success',
          onClick: () => setBulkOpen(true),
        },
        {
          key: 'clear-selection',
          label: 'Clear selection',
          onClick: () => setSelected([]),
        },
      ]);
    } else if (currentTab.key === 'pending') {
      setActions([
        {
          key: 'select-all',
          label: 'Select all visible',
          onClick: () => setSelected(filtered.map((r) => r.vendor_id)),
        },
      ]);
    } else {
      setActions([]);
    }
    return () => setActions([]);
  }, [currentTab.key, selected, filtered, setActions]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Approvals
          </Typography>
          <Chip size="small" label={rows.length} />
        </Stack>
        <Button onClick={load} startIcon={<RefreshIcon />} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {STATUS_TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by name, email, booth…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <ApprovalsQueueTable
        rows={filtered}
        allRows={rows}
        loading={loading}
        status={currentTab.key}
        onChange={load}
        showSelection={currentTab.key === 'pending'}
        selected={selected}
        onSelectionChange={setSelected}
        emptyText={`No vendors in the ${currentTab.label.toLowerCase()} queue.`}
      />

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk approve {selected.length} vendor(s)</DialogTitle>
        <DialogContent>
          {bulkError ? (
            <Typography color="error" sx={{ mb: 1 }}>
              {bulkError}
            </Typography>
          ) : null}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {bulkMarkAsPaid
              ? 'All selected vendors will be approved, marked as paid, and sent confirmation emails (not payment requests).'
              : 'All selected vendors will be approved and notified via email to complete payment.'}
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={bulkMarkAsPaid}
                onChange={(e) => setBulkMarkAsPaid(e.target.checked)}
              />
            }
            label="Mark as already paid (festival pre-registration)"
            sx={{ mb: 2 }}
          />
          <TextField
            label="Notes (optional)"
            value={bulkNotes}
            onChange={(e) => setBulkNotes(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkApprove}
            variant="contained"
            color={bulkMarkAsPaid ? 'primary' : 'success'}
            disabled={bulkLoading}
            startIcon={bulkLoading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
          >
            {bulkMarkAsPaid ? `Approve & mark paid (${selected.length})` : `Approve ${selected.length}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ApprovalsPage;
